import { CHAT_ORCHESTRATOR_FLAG } from "../featureFlagNames";
import { isFeatureFlagEnabled } from "../featureFlags";
import * as z from "zod";
import { type Context, userFacingError } from "../authorization";
import type { Transaction } from "@rocicorp/zero";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { clientGeneratableIdSchema } from "../id";
import { interruptChat } from "./interruptChat";
import { interruptLiveChat } from "./liveChat";
import { mutator } from "./mutator";

export const stopSubAgentSchema = z.object({
  subAgentId: z.string(),
  entryId: clientGeneratableIdSchema,
});

async function subAgentBranchChat(tx: Transaction<Schema>, branchId: string) {
  const branch = await tx.run(zql.workflowBranches.where("id", branchId).one());
  return (
    (await tx.run(
      zql.chats
        .where("respondingAt", "IS NOT", null)
        .whereExists("commit", (commit) => commit.where("branchId", branchId))
        .orderBy("respondingAt", "desc")
        .one()
    )) ??
    (await tx.run(
      zql.chats.where("commitId", branch?.headCommitId ?? "").one()
    ))
  );
}

export async function subAgentChildChatId(
  tx: Transaction<Schema>,
  agent: { branchId: string | null; liveChatId: string | null }
) {
  if (agent.liveChatId) return agent.liveChatId;
  return agent.branchId
    ? (await subAgentBranchChat(tx, agent.branchId))?.id
    : undefined;
}

export async function interruptSubAgentChild(
  tx: Transaction<Schema>,
  ctx: Context,
  agent: {
    workflowId: string;
    branchId: string | null;
    liveChatId: string | null;
  },
  entryId: string
) {
  const chatId = await subAgentChildChatId(tx, agent);
  if (!chatId) throw userFacingError("Chat not found");
  if (agent.branchId)
    await interruptChat.fn({
      tx,
      ctx,
      args: { branchId: agent.branchId, chatId, entryId },
    });
  else
    await interruptLiveChat.fn({
      tx,
      ctx,
      args: { workflowId: agent.workflowId, chatId, entryId },
    });
}

export const stopSubAgent = mutator({
  description:
    "Stops a workflow assistant started from the user’s personal chat.",
  interactionType: "update",
  schema: stopSubAgentSchema,
  resolve: {},
  permissions: {
    none: "The owner is checked before the child interruption enforces workflow access.",
  },
  fn: async ({ tx, ctx, args }) => {
    if (!(await isFeatureFlagEnabled(tx, CHAT_ORCHESTRATOR_FLAG, ctx.tenantId)))
      throw userFacingError("This feature is not available");
    const agent = await tx.run(
      zql.subAgents
        .where("id", args.subAgentId)
        .where("userId", ctx.userId)
        .one()
    );
    if (!agent) throw userFacingError("Agent not found");
    const parent = await tx.run(
      zql.personalChats
        .where("chatId", agent.parentChatId)
        .where("userId", ctx.userId)
        .one()
    );
    if (!parent) throw userFacingError("Chat not found");
    if (agent.status !== "running" && agent.status !== "needs_input") return;
    await interruptSubAgentChild(tx, ctx, agent, args.entryId);
  },
});
